import { HoverableLinkSizes } from '@paywithextend/components'
import React, { ReactElement, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import {
  selectCreditCardStatisticsActiveBudgetCount,
  selectCreditCardStatisticsActiveVcnCount,
  selectCreditCardStatisticsBudgetCount,
} from 'src/app/Account/redux/account.selectors'
import {
  selectAccountResendVerificationError,
  selectAccountVerificationError,
  selectPostingAccountVerification,
  selectPostingResendAccountVerification,
} from 'src/app/AccountOverview/redux/accountOverview.selectors'
import { amexIssuerCodes } from 'src/app/RegisterAccount/RegisterAccount'
import AccountVerificationBlockedBox from 'src/components/AccountVerificationBlockedBox/AccountVerificationBlockedBox'
import AccountVerificationRequiredBox from 'src/components/AccountVerificationRequiredBox/AccountVerificationRequiredBox'
import AccountVerificationTimedOutBox from 'src/components/AccountVerificationTimedOutBox/AccountVerificationTimedOutBox'
import ActiveUntilBox from 'src/components/ActiveUntilBox/ActiveUntilBox'
import AmexAccountVerificationActionRequiredBox from 'src/components/AmexAccountVerificationActionRequiredBox/AmexAccountVerificationActionRequiredBox'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import BudgetsBox from 'src/components/BudgetsBox/BudgetsBox'
import ObjectBoxTitle from 'src/components/ObjectBoxTitle/ObjectBoxTitle'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import { Issuers } from 'src/enums'
import CardBudgetsTable from 'src/feature/CardBudgetsTable/CardBudgetsTable'
import CardOverviewCardDetails from 'src/feature/CardOverviewCardDetails/CardOverviewCardDetails'
import CardOverviewRecentTransactions from 'src/feature/CardOverviewRecentTransactions/CardOverviewRecentTransactions'
import CardOverviewSpendByCategory from 'src/feature/CardOverviewSpendByCategory/CardOverviewSpendByCategory'
import CardOverviewStats from 'src/feature/CardOverviewStats/CardOverviewStats'
import CardVirtualCardsTable from 'src/feature/CardVirtualCardsTable/CardVirtualCardsTable'
import { settingsAnchors } from 'src/feature/UpdatedPhysicalCardSettings/settingsAnchors'
import { useTranslate } from 'src/localization/Trns'
import {
  CreditCardListModel,
  CreditCardStatus,
  CreditCardType,
} from 'src/models/creditCard.models'
import { CreditCardPermissionsModel } from 'src/models/creditCardPermissions.models'
import { VirtualCardStatus } from 'src/models/virtualCard.models'
import {
  accountBudgetsUrl,
  accountSettingsUrl,
  accountTransactionsUrl,
  accountVirtualCardsUrl,
  budgetOverviewUrl,
  transactionDetailUrl,
  virtualCardDetailUrl,
} from 'src/navigation/urls'
import * as style from 'src/page-components/Accounts/AccountOverviewTab/accountOverviewTab.style'
import { useQueryCreditCards } from 'src/queries/creditCard.queries'
import {
  useStartCreditCardVerification,
  useVerifyCreditCard,
} from 'src/queries/creditCards/creditCardWrites.queries'
import { transactionsQueryKeys } from 'src/queries/queries.constants'
import { useQueryBudgetVirtualCardCounts } from 'src/queries/statistics/budgetVirtualCardCounts.queries'
import { useQueryVirtualCards } from 'src/queries/virtualCards.queries'
import { queryClient } from 'src/queryClient'

/** BudgetsBox only shows its empty state once told the list is complete. */
const NOOP = (): void => undefined

/** Rows in the redesigned Budgets snapshot, per the design. */
const UPDATED_BUDGETS_SNAPSHOT_COUNT = 5
/** What legacy `AccountOverview` asked for, kept for the flag-off path. */
const LEGACY_BUDGETS_SNAPSHOT_COUNT = 3
/**
 * Rows in the redesigned Virtual Cards snapshot. Five, matching the design and
 * its two sibling panels — legacy `VirtualCardsBox` asked for eight, sized for a
 * grid of card tiles rather than a table.
 */
const VIRTUAL_CARDS_SNAPSHOT_COUNT = 5

/**
 * Which sibling tabs the card actually has.
 *
 * One flag per tab, NOT one flag for "is this a full-featured card": the three
 * subnav conditions genuinely differ (Budgets needs `isActive`, Virtual Cards
 * admits `isClosed`, Transactions admits physical-only), so a single boolean
 * standing in for all three silently mismatches at least two of them.
 */
export interface AccountTabAvailability {
  budgets: boolean
  virtualCards: boolean
  transactions: boolean
}

interface AccountOverviewTabProps {
  /**
   * The card whose overview to render. The page already holds it, so passing it
   * down avoids a second `useQueryCreditCard` observer for the same entity.
   */
  creditCard: CreditCardListModel
  /** Drives whether the verification box offers a retry. */
  creditCardPermissions?: CreditCardPermissionsModel
  /**
   * Gates every surface here that links OUT to a sibling tab: the roll-up's
   * Virtual cards cell and budgets footer, the Budgets Created / Active Cards
   * grids, and Recent Transactions' "view all" link.
   *
   * Computed by the page from the same expressions its subnav pushes tabs
   * from, and passed down rather than re-derived here even though this holds
   * `creditCard` and could: deriving it in both places is how the tab bar and
   * the links inside it drift apart.
   *
   * Note this gates the LINKS, not the content. A pending card still gets its
   * real transaction list; it just does not get a "view all" pointing at a tab
   * it does not have.
   */
  tabAvailability: AccountTabAvailability
  /**
   * EX-40737 — swaps the Budgets snapshot for the redesigned `CardBudgetsTable`.
   *
   * Computed by the page (flag OR the Card Management view capability) and
   * passed down rather than re-read here, because the Budgets TAB reads the same
   * gate: the snapshot and the full list must swap together or the same rows
   * report different columns either side of one click.
   */
  showUpdatedBudgetsTable: boolean
}

/**
 * Redesigned Overview tab content for the card-detail page (EX-40632).
 *
 * This is the routing boundary for the tab: it reads the per-card statistics,
 * builds every destination URL, and hands plain values to `CardOverviewStats`,
 * which stays routing-free.
 *
 * THE STATS ROLL-UP IS FULLY LIVE as of EX-40596: every figure reads a
 * capability-gated endpoint, and anything unreadable renders an em dash instead
 * of a stand-in. The payment and statement endpoints are still mock-backed
 * server-side, which the roll-up marks with its own provenance indicator.
 *
 * ONE PANEL IS STILL FABRICATED — the spend-by-category chart. There is no
 * per-card category breakdown in the API at all (see
 * `cardOverviewSpendByCategoryDemoData.ts`), and nothing on screen marks it as
 * invented. Read that module before enabling this tab anywhere a customer could
 * mistake the chart for their own spend.
 *
 * ONE REDIRECT IS PARITY SURFACE. The legacy `AccountOverview` this replaces
 * sends budget cards to the Budget Overview, and the caller
 * (`AccountEntityPage`) reproduces that BEFORE this renders — losing it would
 * land a budget card on a physical card's summary.
 *
 * Legacy ALSO redirected physical-only cards to Account Settings. That one was
 * dropped on purpose: the redesigned surface is card-level, so those cards get
 * this tab instead, minus the virtual-card and budget sections they have no
 * tabs for. See `tabAvailability` and the note in
 * `AccountEntityPage`.
 *
 * LAYOUT: stats roll-up → the legacy Overview sections → recent transactions.
 *
 * The legacy sections are lifted from `AccountOverviewBackgroundPageContent`
 * rather than left behind, so the redesigned tab is a SUPERSET of the one it
 * replaces and the legacy path can eventually be deleted. Carried over:
 * Budgets Created, Active Cards, and all four verification boxes. The legacy
 * status box was carried over initially but was removed once the roll-up
 * stats bar (`CardOverviewStats`) covered its content — see the "remove
 * summary bar from new overview" design decision.
 * Deliberately dropped: the "get started" promotion boxes and the
 * `NoAccountActivity` splash, which the stats roll-up now serves.
 *
 * NOT carried over: legacy's 30-day spend graph. `CardOverviewSpendByCategory`
 * holds that slot instead — a different measure, not a restyling of the same
 * one, and its figures are FABRICATED. Read its demo-data file before enabling
 * this anywhere a customer can reach it. The legacy path keeps its own graph.
 *
 * Unlike legacy, the Budgets and Active Cards boxes render at zero with their
 * own empty states instead of disappearing.
 */
export default function AccountOverviewTab(
  props: AccountOverviewTabProps,
): ReactElement {
  const {
    creditCard,
    creditCardPermissions,
    tabAvailability,
    showUpdatedBudgetsTable,
  } = props
  const creditCardId = creditCard.id
  const history = useHistory()
  const translate = useTranslate()

  // Populated as a side effect of `creditCardQueries.detail`, which the parent
  // page already fetches — so reading them here costs no extra request. They are
  // the legacy per-credit-card statistics family, deliberately left on Redux
  // (see src/queries/AGENTS.md); `undefined` until they land, which the roll-up
  // renders as an em dash rather than a zero.
  const virtualCardCount = useSelector(selectCreditCardStatisticsActiveVcnCount)
  const budgetCount = useSelector(selectCreditCardStatisticsBudgetCount)
  // The other half of the same legacy statistics family, read for the two
  // legacy section headers' "N of M" proportionals.
  const activeBudgetCount = useSelector(
    selectCreditCardStatisticsActiveBudgetCount,
  )

  // Both lists mirror legacy AccountOverview's queries exactly — same filters,
  // same page sizes — so the boxes below get the data they were built for.
  //
  // Gated on the same flag as the box it feeds: a card that does not render
  // Budgets Created should not fetch a delegate list whose result is
  // discarded. Safe to state as the whole `enabled` — `useQueryCreditCards`
  // ANDs the factory's own arg-presence guard in itself
  // (`enabled && filters !== undefined`, creditCard.queries.tsx), so
  // overriding here does not drop it.
  //
  // `tabAvailability.budgets` is already a strict boolean, which matters:
  // `features.budgets` is `boolean | undefined` and `useQueryCreditCards`
  // defaults `enabled = true`, so passing the raw field fired the query for
  // every card whose detail response simply omitted the feature.
  const {
    data: budgetsData,
    isFetched: budgetsFetched,
    isError: budgetsError,
  } = useQueryCreditCards({
    filters: {
      status: CreditCardStatus.Active,
      type: CreditCardType.Delegate,
      // EX-40737 — the redesigned snapshot shows five rows, matching the design
      // (and the sibling recent-transactions panel). Conditional rather than
      // just raised: `BudgetsBox` reads this same query on the flag-off path,
      // and that path has to stay exactly what legacy shipped — three rows.
      count: showUpdatedBudgetsTable
        ? UPDATED_BUDGETS_SNAPSHOT_COUNT
        : LEGACY_BUDGETS_SNAPSHOT_COUNT,
      page: 0,
      sortField: 'activeUpdatedAt',
      sortDirection: 'ASC',
      parentCreditCard: creditCardId,
    },
    enabled: tabAvailability.budgets,
    // Same override, and the same reason, as the recent-transactions panel:
    // the list factory sets `placeholderData: keepPreviousData`, and
    // `AccountEntityPage` renders this tab from a keyless <Route>, so moving
    // between two card overviews re-renders rather than remounts. Without this
    // the PREVIOUS card's budgets paint under the new card's heading with
    // `isFetched` already true.
    placeholderData: undefined,
  })

  const budgets = useMemo(
    () => budgetsData?.creditCards ?? [],
    [budgetsData?.creditCards],
  )
  // EX-40737 — the redesigned table's "Cards" column, one batched POST for the
  // whole snapshot. Passing `[]` while the legacy table is showing keeps the
  // query disabled, so the flag-off path costs no extra request.
  const budgetIds = useMemo(
    () => (showUpdatedBudgetsTable ? budgets.map((budget) => budget.id) : []),
    [showUpdatedBudgetsTable, budgets],
  )
  const budgetCardCounts = useQueryBudgetVirtualCardCounts(budgetIds)

  const {
    data: virtualCardsData,
    isFetched: virtualCardsFetched,
    isError: virtualCardsError,
  } = useQueryVirtualCards({
    filters: { statuses: [VirtualCardStatus.Active], creditCardId },
    perPage: VIRTUAL_CARDS_SNAPSHOT_COUNT,
    // Same reasoning as the budgets list above: a card that will not render
    // Active Cards should not fetch the rows for it.
    enabled: tabAvailability.virtualCards,
    // And the same placeholder override, for the same reason — otherwise the
    // previous card's virtual cards render under the new card's heading.
    placeholderData: undefined,
  })

  // Verification state stays on Redux — these are legacy thunk-backed flags the
  // verification boxes were built against.
  const postingAccountVerification = useSelector(
    selectPostingAccountVerification,
  )
  const accountVerificationError = useSelector(selectAccountVerificationError)
  const postingResendAccountVerification = useSelector(
    selectPostingResendAccountVerification,
  )
  const accountResendVerificationError = useSelector(
    selectAccountResendVerificationError,
  )
  const verifyMutation = useVerifyCreditCard()
  const startVerificationMutation = useStartCreditCardVerification()

  const handleVerifyCreditCard = useCallback(
    (code: string): void => {
      verifyMutation.mutate({ creditCardId, body: { code } })
    },
    [verifyMutation, creditCardId],
  )
  const handleResendVerification = useCallback((): void => {
    startVerificationMutation.mutate({ creditCardId })
  }, [startVerificationMutation, creditCardId])

  // Section visibility, mirroring legacy AccountOverviewBackgroundPageContent.
  const isAmex = amexIssuerCodes.includes(creditCard.issuer as Issuers)
  const showBlocked = creditCard.isBlocked
  const showAmexActionRequired =
    isAmex &&
    creditCard.isPending &&
    !creditCard.features.needsCardholderVerification
  const showTimedOut = creditCard.status === CreditCardStatus.Inactive && isAmex
  const showVerificationRequired =
    creditCard.isPending && creditCard.features.needsCardholderVerification
  // Legacy also required the virtual-card list to have loaded before showing
  // anything; kept, so the sections appear together rather than popping in.
  //
  // But only when that list is actually being fetched. The query is gated on
  // `tabAvailability.virtualCards`, and a DISABLED query never reports
  // `isFetched` — so without this clause an active physical-only card waits
  // forever on a request that will never be made, and loses Active Until and
  // the spend-by-category pairing. Those are card-level sections it should
  // keep; it is only the Virtual Cards / Budgets snapshot it should lose.
  const showSections =
    (creditCard.isActive || creditCard.isClosed) &&
    (!tabAvailability.virtualCards || virtualCardsFetched) &&
    !showBlocked
  // Legacy rendered a verification box and nothing else in every unverified
  // state, and the roll-up follows it. Half of what the roll-up shows is
  // fabricated and NOTHING on screen says so; sitting invented balances and
  // limits directly above a "verify your account" prompt reads as this card's
  // real money, precisely where the user has no way to check.
  //
  // All three unverified states, not just pending: a blocked card (too many
  // failed attempts) and a timed-out Amex card are the same pairing, and the
  // reason to withhold it does not weaken for either. `showAmexActionRequired`
  // is a subset of `isPending`, so it is covered without its own clause.
  //
  // The real fix is removing the fabricated figures — EX-40688. This is the
  // interim containment, and it goes away with them.
  //
  // Recent transactions below stays outside this — a real, empty-or-not list is
  // honest at any card state. Only the fabricated figures are the problem.
  const showStatsRollUp = !creditCard.isPending && !showBlocked && !showTimedOut

  // `push`, not `replace` — these are ordinary forward navigations and the user
  // must be able to come back to Overview.
  const handleNavigate = useCallback(
    (href: string): void => {
      history.push(href)
    },
    [history],
  )

  // The budget detail page, the same destination legacy's `BudgetsBox` rows
  // carried. `push`, not `replace` — the user must be able to come back.
  const handleSelectBudget = useCallback(
    (budgetId: string): void => {
      history.push(budgetOverviewUrl(budgetId))
    },
    [history],
  )

  // The virtual card detail page, the same destination legacy's
  // `VirtualCardsBox` tiles carried.
  const handleSelectVirtualCard = useCallback(
    (virtualCardId: string): void => {
      history.push(virtualCardDetailUrl(virtualCardId))
    },
    [history],
  )

  const handleSelectTransaction = useCallback(
    (transactionId: string, isSplit: boolean): void => {
      // Matches TransactionsDataGrid: a split transaction's cached detail is
      // stale relative to the flyout that is about to read it.
      if (isSplit) {
        queryClient.removeQueries({
          queryKey: transactionsQueryKeys.detail(transactionId),
        })
      }
      // The explicit empty search/hash/state is the location shape the
      // transaction flyout expects — pushing a bare string would let this
      // route inherit the current location's state.
      history.push({
        pathname: transactionDetailUrl(transactionId),
        search: '',
        hash: '',
        state: {},
      })
    },
    [history],
  )

  /*
    EX-40630 — Card details. Rendered UNCONDITIONALLY, not behind
    `showStatsRollUp`: that gate withholds the roll-up's *fabricated* figures,
    and nothing here is fabricated. The capability gate and the panel's four
    states already tell the truth at any card state. Last four and expiration
    come off the already-loaded card; the panel is routing-free, so the page
    builds the Controls & Settings href.

    Held in a function of the optional class rather than a plain element
    because it renders from two places below — paired with the spend-by-category
    chart, or full width on its own — and duplicating the props is how the two
    paths silently drift apart. The class parameter exists so ONLY the paired
    branch can opt this panel out of the details row's `align-items: stretch`
    (see `CARD_DETAILS_CELL_CLASS` in `accountOverviewTab.style.ts`) — the
    full-width fallback below must never receive it, since that box sits in a
    column flex container where `align-self` acts on the horizontal axis.
  */
  const renderCardDetails = (className?: string): ReactElement => (
    <CardOverviewCardDetails
      creditCardId={creditCardId}
      last4={creditCard.last4}
      expiration={creditCard.expiresFormattedString}
      manageHref={`${accountSettingsUrl(creditCardId)}#${
        settingsAnchors.cardSettings
      }`}
      onNavigate={handleNavigate}
      className={className}
    />
  )

  return (
    // Every sibling tab supplies its own BackgroundPageContent — that is where
    // the page gutters live, so without it this renders flush to the viewport.
    //
    // `noStackingIndex` on a pending card is carried over from legacy
    // (`AccountOverviewBackgroundPageContent`), where commit 43212f7d27 added it
    // specifically for that case. This PR is what puts pending-card content —
    // the verification boxes — on this tab, so the prop comes with it rather
    // than being dropped as incidental.
    <BackgroundPageContent noStackingIndex={creditCard.isPending}>
      <div css={style.sections} data-testid="accountOverviewTab-content">
        {showStatsRollUp && (
          <CardOverviewStats
            creditCardId={creditCardId}
            // Two flags, not one: the Virtual cards cell and the budgets
            // footer link to different tabs, whose conditions differ (a closed
            // card keeps Virtual Cards but loses Budgets).
            showVirtualCards={tabAvailability.virtualCards}
            showBudgets={tabAvailability.budgets}
            virtualCardCount={virtualCardCount}
            budgetCount={budgetCount}
            // The fragment targets the Spend Limit tile, and `AccountEntityPage`
            // is what acts on it — `history.push` alone performs no fragment
            // navigation. It only resolves when
            // `show_updated_physical_card_settings` is on; with that flag off the
            // link still lands on the right tab, just at the top. See
            // `settingsAnchors.ts`.
            adjustLimitHref={`${accountSettingsUrl(creditCardId)}#${
              settingsAnchors.spendLimit
            }`}
            virtualCardsHref={accountVirtualCardsUrl(creditCardId)}
            budgetsHref={accountBudgetsUrl(creditCardId)}
            onNavigate={handleNavigate}
          />
        )}

        {/*
          Card details beside Spend by category, directly under the stats roll-up
          per the agreed layout (KPI band → CardIdentity | spend → …).

          The pairing still follows `showSections`, but the reason has changed
          and is worth stating plainly. The 30-day spend graph that used to hold
          this slot OBEYED that gate out of necessity — it had no data for a
          pending or blocked card. Spend by category renders from a fixture, so
          it has no such dependency and would happily draw on any card. It is
          gated anyway, as a deliberate choice: a chart of invented category
          spend beside a "verify your account" prompt reads as this card's real
          money precisely where the user cannot check it — the same reasoning
          that withholds the fabricated half of the stats roll-up.

          The layout argument holds independently. Card details renders at ANY
          card state, so pairing unconditionally would strand it in a third of
          the row beside dead space whenever its partner is absent, which is
          worse than the full-width fallback below.

          This keeps the chart ABOVE the status and Active Until boxes, where the
          graph was lifted to. Deliberate, and the reason those two stay behind in
          their own gate rather than joining the row.
        */}
        {showSections ? (
          <div
            css={style.detailsRow}
            data-testid="accountOverviewTab-detailsRow"
          >
            {renderCardDetails(style.CARD_DETAILS_CELL_CLASS)}
            <CardOverviewSpendByCategory />
          </div>
        ) : (
          renderCardDetails()
        )}

        {showSections && creditCard.isClosed && (
          <ActiveUntilBox
            inactiveSinceString={creditCard.inactiveSinceTimezonedString}
            activeUntilString={creditCard.activeUntilTimezonedString}
            openLeftMdUp={false}
            openLeftSmDown
            isAccountClosed
          />
        )}

        {/*
          EX-40633 — directly under the spend graph and first of the three
          tables, per the agreed layout: transactions -> virtual cards ->
          budgets. The PANEL is outside the `showSections`
          gate on purpose: that gate is legacy parity for the legacy boxes, and
          a blocked or pending card still has transactions worth showing.

          Its "view all" LINK is gated separately, because a pending card has
          the transactions but not the Transactions tab
          (`isActive || isClosed || physicalOnly`). Omitting the href leaves the
          list intact and drops only the link — the content is honest at any
          card state; only the navigation is conditional.
        */}
        <CardOverviewRecentTransactions
          creditCardId={creditCardId}
          viewAllHref={
            tabAvailability.transactions
              ? accountTransactionsUrl(creditCardId)
              : undefined
          }
          onNavigate={handleNavigate}
          onSelectTransaction={handleSelectTransaction}
        />

        {/*
          Virtual cards then budgets, completing the agreed table order below
          recent transactions.

          Both carry links out to the Virtual Cards and Budgets tabs, so each
          follows its OWN tab's availability rather than a shared
          "is this a full card" flag — the two conditions differ, and a closed
          card is exactly where they diverge: it keeps Virtual Cards and loses
          Budgets.

          `showSections` alone is not enough for either: it reads
          `displayStatus`, which has no NOT_APPLICABLE member, so a
          physical-only card reports ACTIVE and falls straight through it.
          Legacy never hit this because it redirected those cards away from
          Overview entirely.

          Kept out of `showSections` on purpose: Active Until and the
          spend-by-category pairing above ARE card-level, and a physical-only
          card should keep them.
        */}
        {showSections && (
          <>
            {/*
              The redesigned Virtual Cards snapshot, replacing legacy's "Active
              Cards" `VirtualCardsBox`. No flag of its own: the whole redesigned
              Overview is already behind `isUpdatedCardOverviewEnabled`, so this
              is unreachable with that off.

              The Virtual Cards TAB still renders the legacy grid. Swapping it
              WILL need its own flag, read on `AccountEntityPage` and passed to
              both call sites the way `showUpdatedBudgetsTable` is — otherwise a
              user who follows "view all virtual cards" finds the same cards
              described by different columns.
            */}
            {tabAvailability.virtualCards && (
              <CardVirtualCardsTable
                virtualCards={virtualCardsData?.virtualCards ?? []}
                // The list query is gated on `tabAvailability.virtualCards`,
                // which is true in this branch, so `isFetched` is a real signal
                // here rather than the permanently-false one a disabled query
                // reports.
                isPending={!virtualCardsFetched}
                // Without this a failed request reads as an empty one: the query
                // reports `isFetched` on error too, so the table would reach its
                // empty state and tell the user an account with cards has none.
                isError={virtualCardsError}
                viewAllHref={accountVirtualCardsUrl(creditCardId)}
                onNavigate={handleNavigate}
                // Rows are real links; the page owns the URL, the table owns the
                // click.
                virtualCardHref={virtualCardDetailUrl}
                onSelectVirtualCard={handleSelectVirtualCard}
                // No New virtual card CTA on the Overview snapshot (UAT). The
                // snapshot's heading carries "view all" and nothing else — the
                // create flow lives on the Virtual Cards tab, which is one
                // click away through that link. The prop stays supported for
                // the tab, which still renders it.
              />
            )}
            {/*
              Legacy's `activeBudgetCount > 0` was doing double duty as the
              capability gate — on an account without the feature the count is
              always zero, so the box was structurally unreachable. Showing the
              empty state at zero removed that gate as a side effect, so it is
              restored explicitly, now via the tab flag (which also carries the
              `isActive` clause the subnav applies and this used to miss).
            */}
            {tabAvailability.budgets && showUpdatedBudgetsTable && (
              <CardBudgetsTable
                budgets={budgets}
                cardCounts={budgetCardCounts}
                // The list query is gated on `tabAvailability.budgets`, which is
                // true in this branch, so `isFetched` is a real signal here
                // rather than the permanently-false one a disabled query reports.
                isPending={!budgetsFetched}
                // Without this a failed request reads as an empty one: the
                // query reports `isFetched` on error too, so the table would
                // reach its empty state and tell the user a card with budgets
                // has none.
                isError={budgetsError}
                viewAllHref={accountBudgetsUrl(creditCardId)}
                onNavigate={handleNavigate}
                // Rows are real links; the page owns the URL, the table owns
                // the click. `budgetOverviewUrl` takes an id and returns a
                // path, which is exactly the prop's shape.
                budgetHref={budgetOverviewUrl}
                onSelectBudget={handleSelectBudget}
                // No New budget CTA on the Overview snapshot (UAT), matching
                // the virtual-cards snapshot above: the heading carries "view
                // all" and nothing else, and the create flow lives on the
                // Budgets tab that link leads to. The prop stays supported for
                // the tab, which still renders it.
              />
            )}
            {tabAvailability.budgets && !showUpdatedBudgetsTable && (
              <BudgetsBox
                budgets={budgets}
                isSlim={false}
                isSnapshot
                defaultTitle={
                  <ObjectBoxTitle
                    title={translate('Budgets Created')}
                    proportional={{
                      viewable: budgets.length,
                      total: activeBudgetCount || 0,
                    }}
                  />
                }
                // Legacy hid this box entirely at zero. We keep it and let it show
                // its empty state, which it only does once told the list is
                // complete — hence this synthetic scroll state rather than none.
                infiniteScrollProps={{
                  fetch: NOOP,
                  loading: !budgetsFetched,
                  complete: budgetsFetched,
                }}
                navAway={{
                  label: translate('All Budgets on this Account'),
                  to: accountBudgetsUrl(creditCardId),
                  ariaLabelSource: 'All Budgets on this Account',
                }}
              />
            )}
          </>
        )}

        {showAmexActionRequired && (
          <AmexAccountVerificationActionRequiredBox creditCard={creditCard} />
        )}
        {showBlocked && (
          <AccountVerificationBlockedBox
            secondParagraph={translate(
              'There have been too many failed attempts at verifying the account. In order to proceed, contact us via our ${resourceCenter}.',
              {
                // Legacy sized this link to match the surrounding paragraph
                // with an inline `css` block (16px regular, repeated for
                // `:hover`). The weight half of that is now handled inside
                // `ResourceCenterLink`, which passes `regular` unconditionally;
                // only the size was left behind. `Large` IS 16px, so this is
                // the same result through the component's own API rather than
                // an inline template — and it matches the sibling
                // `AccountVerificationTimedOutBox`.
                resourceCenter: (
                  <ResourceCenterLink
                    underline
                    size={HoverableLinkSizes.Large}
                  />
                ),
              },
            )}
          />
        )}
        {showTimedOut && <AccountVerificationTimedOutBox />}
        {showVerificationRequired && (
          <AccountVerificationRequiredBox
            accountResendVerificationError={accountResendVerificationError}
            accountVerificationError={accountVerificationError}
            canVerifyAgain={creditCard.canVerifyAgain}
            handleResendVerification={handleResendVerification}
            handleVerifyCreditCard={handleVerifyCreditCard}
            postingAccountVerification={postingAccountVerification}
            postingResendAccountVerification={postingResendAccountVerification}
            timeUntilNextVerificationRequested={
              creditCard.timeUntilNextVerificationRequested
            }
            canUpdateCreditCard={creditCardPermissions?.canUpdateCreditCard}
          />
        )}
      </div>
    </BackgroundPageContent>
  )
}
